<template>
  <!-- Mirrored by _language_menu_html in backend/app.py. A <details> opens and
       closes without JavaScript, so the menu works in the server-rendered page
       too, and its links stay crawlable. -->
  <details ref="menu" class="lang-menu">
    <summary class="lang-menu-button" :aria-label="`${$t('nav.language')}: ${currentName}`">{{ $locale.toUpperCase() }}</summary>
    <ul class="lang-menu-list">
      <li v-for="item in items" :key="item.code">
        <span v-if="item.current" class="lang-menu-current" aria-current="true">{{ item.name }}</span>
        <router-link
          v-else
          :to="item.path"
          :hreflang="item.hreflang"
          :lang="item.code"
          rel="alternate"
          @click.native="close">{{ item.name }}</router-link>
      </li>
    </ul>
  </details>
</template>

<script>
import { languageMenuItems, localeInfo } from '../i18n/locales';

export default {
  props: {
    // This page without a language prefix, e.g. '/guides/emdr-phases'
    path: { type: String, required: true },
    // Languages the page exists in; all of them when not given
    languages: { type: Array, default: undefined }
  },
  computed: {
    items() {
      return languageMenuItems(this.path, this.$locale, this.languages);
    },
    currentName() {
      return localeInfo(this.$locale).name;
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    close() {
      this.$refs.menu.open = false;
    },
    onDocumentClick(event) {
      if (!this.$refs.menu.contains(event.target)) this.close();
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.$refs.menu.open) {
        this.close();
        this.$refs.menu.querySelector('summary').focus();
      }
    }
  }
};
</script>

<style>
/* Not scoped: the server renders the same markup before the app mounts. */
.lang-menu {
  position: relative;
}
.lang-menu-button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.2rem 0.6rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  list-style: none;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
.lang-menu-button::-webkit-details-marker {
  display: none;
}
.lang-menu-button::after {
  content: '';
  width: 0.38em;
  height: 0.38em;
  border-right: 1.5px solid currentColor;
  border-bottom: 1.5px solid currentColor;
  transform: translateY(-25%) rotate(45deg);
}
.lang-menu[open] .lang-menu-button::after {
  transform: translateY(15%) rotate(-135deg);
}
.lang-menu-button:hover,
.lang-menu[open] .lang-menu-button {
  color: var(--c-accent-light);
  border-color: var(--c-accent-light);
}
.lang-menu-list {
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  z-index: 30;
  min-width: 9.5rem;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  background: #16302c;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
}
.lang-menu-list a,
.lang-menu-current {
  display: block;
  padding: 0.45rem 0.7rem;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.88rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.lang-menu-list a:hover,
.lang-menu-list a:focus-visible {
  background: rgba(255, 255, 255, 0.08);
  color: var(--c-accent-light);
}
.lang-menu-current {
  color: white;
  font-weight: 600;
}
.lang-menu-current::after {
  content: '✓';
  margin-left: 0.5rem;
  color: var(--c-accent-light);
}
</style>
